/**
 * Selo — o distintivo ao lado do nome. Fonte: `Selo.dc.html`.
 *
 * Cinco variantes, e a ordem de precedência importa:
 *
 *   nutri     folha, gradiente verde  — CRN verificado
 *   personal  halter, gradiente azul  — CREF verificado
 *   check     check, gradiente brand  — assinante verificado
 *   hex       check, hexágono dourado — nível alto
 *   star      estrela, azul           — destaque
 *   pill      "PRO" escrito           — etiqueta textual
 *
 * O SELO PROFISSIONAL SUBSTITUI O COMUM onde os dois competiriam (§ 7 do
 * handoff 15): duas bolinhas ao lado do mesmo nome não dizem duas coisas, só
 * poluem. `seloDoUsuario` abaixo é quem decide, para que a regra viva num
 * lugar só e não em cada tela que mostra um usuário.
 *
 * O ícone é desenhado com `currentColor` em branco sobre o gradiente — não há
 * arquivo de asset por variante.
 */

import React from 'react';

import cx from '../../cx';
import s from './Selo.module.scss';

/** Traços de cada variante, no viewBox 24. */
const CAMINHOS = {
  check: 'M20 6 9 17l-5-5',
  hex: 'M20 6 9 17l-5-5',
  nutri: 'M11 20.5c-4.5 0-7-3-7-7 0-5 4-8 9.5-8.5 2-.2 3.5-1 4.5-2.5 1 2.5 1.5 5 1.5 7.5 0 6-3.5 10.5-8.5 10.5Z',
  personal: 'M6.8 8.5v7M17.2 8.5v7M6.8 12h10.4M3.4 10v4M20.6 10v4',
};

/** A folha tem um segundo traço (a nervura); o resto tem um só. */
const CAMINHO_EXTRA = { nutri: 'M12 20c0-4.5 1.6-8 4.5-10.5' };

const ESTRELA = 'M12 2l2.9 6.3 6.9.8-5.1 4.7 1.4 6.8L12 17.3 5.9 20.6l1.4-6.8L2.2 9.1l6.9-.8z';

/** Rótulo acessível por variante. */
const ROTULO = {
  nutri: 'Nutricionista verificado',
  personal: 'Personal trainer verificado',
  check: 'Perfil verificado',
  hex: 'Perfil verificado',
  star: 'Destaque',
  pill: 'PRO',
};

/**
 * Qual selo mostrar para um usuário, dado o recorte público da conta
 * profissional e se ele é verificado por assinatura.
 *
 * REGRA ÚNICA, num lugar só: o profissional ganha do comum. Espalhar este
 * `if` por comentário, feed, perfil e busca é como as telas divergem.
 *
 * @param {object|null} pro recorte público (`{status}`) vindo do backend
 * @param {boolean} verificado assinante verificado
 * @returns {string|null} variante do selo, ou null
 */
export function seloDoUsuario(pro, verificado = false) {
  if (pro?.status === 'nutri') return 'nutri';
  if (pro?.status === 'personal') return 'personal';
  return verificado ? 'check' : null;
}

export default function Selo({ tipo = 'check', tamanho = 16, className = '', ...rest }) {
  const variante = CAMINHOS[tipo] || tipo === 'star' || tipo === 'pill' ? tipo : 'check';
  const lado = Number(tamanho) || 16;

  // Hexágono é 10% mais alto que largo — é a proporção do desenho.
  const altura = variante === 'hex' ? Math.round(lado * 1.1) : lado;

  return (
    <span
      className={cx(s.selo, s[variante], className)}
      /* Tamanho é parâmetro, não token: o mesmo selo aparece em 14 no
         comentário e em 30 no hero. */
      style={{ width: variante === 'pill' ? 'auto' : lado, height: altura }}
      role="img"
      aria-label={ROTULO[variante] || ROTULO.check}
      {...rest}
    >
      {variante === 'pill' && (
        <span className={s.texto} style={{ fontSize: Math.round(lado * 0.55) }}>PRO</span>
      )}

      {variante === 'star' && (
        <svg viewBox="0 0 24 24" width={lado * 0.6} height={lado * 0.6} fill="#fff" aria-hidden="true">
          <path d={ESTRELA} />
        </svg>
      )}

      {CAMINHOS[variante] && (
        <svg
          viewBox="0 0 24 24"
          width={lado * 0.62}
          height={lado * 0.62}
          fill="none"
          stroke="#fff"
          strokeWidth="3.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d={CAMINHOS[variante]} />
          {CAMINHO_EXTRA[variante] && <path d={CAMINHO_EXTRA[variante]} strokeWidth="2.2" />}
        </svg>
      )}
    </span>
  );
}
